import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ChevronLeft, Terminal, Loader2, CheckCircle2, Database,
  ShieldCheck, Workflow, Target, Layers, FlaskConical, Gauge,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";

// Phase 26.2 — AI Training Data vertical. ABM-style page targeting AI labs
// (Mercor / Scale-type buyers): DARA as the expert-data pipeline partner.
// IP posture: no vendor/model names. "DARA Engine" / "data pipelines" framing.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAPABILITIES = [
  {
    icon: Database,
    title: "Expert-annotated corpora",
    body: "Drill assays graded by geologists. Compliance decisions labelled by licensed officers. Claims triaged by underwriters. Data your crowd-sourcing vendor cannot produce at any price.",
  },
  {
    icon: FlaskConical,
    title: "Eval & benchmark sets",
    body: "Held-out, contamination-free test sets from live operational workflows — the hardest domains to benchmark are the ones we already run pipelines inside.",
  },
  {
    icon: Workflow,
    title: "Human-feedback pipelines",
    body: "Structured preference and correction data captured at the point of real decisions — not synthetic rater farms. Full task context travels with every label.",
  },
  {
    icon: ShieldCheck,
    title: "Provenance & consent chain",
    body: "Every datum carries its consent basis, jurisdiction, and lineage. Procurement-grade paperwork ready for your counsel on day one.",
  },
];

const VERTICALS = [
  { name: "Mineral exploration", detail: "Assay tables · JORC materiality calls · drill-program decisions" },
  { name: "Wagering compliance", detail: "Regulatory rulings · risk flags · obligation mappings" },
  { name: "Industrial operations", detail: "Telemetry anomalies · maintenance call-outs · shift logs" },
  { name: "Insurance & fire safety", detail: "Claims narratives · certification checklists · inspection outcomes" },
  { name: "SMB point-of-sale", detail: "Transaction streams · void/refund patterns · demand signals" },
  { name: "Development & aid data", detail: "Allocation decisions · programme outcomes · field reporting" },
];

const STEPS = [
  { n: "01", title: "Scope the dataset", body: "You bring the capability gap — domain, task shape, volume, eval criteria. We map it to pipelines we already operate." },
  { n: "02", title: "Pipeline activation", body: "Our in-vertical experts generate, label, and QA against your spec. Weekly drops, versioned, with inter-rater reliability scores." },
  { n: "03", title: "Delivery & iteration", body: "Clean handoff in your schema. We iterate on edge-case mining from your model's failure modes." },
];

export default function AITrainingData() {
  usePageview("/ai-training");

  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.email.trim()) {
      toast.error("Work email is required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        industry: "ai-training",
        ...form,
        source: "ai-training-page",
        visitor_session: getVisitorSession(),
      });
      setDone(true);
      toast.success("Received. We'll come back within one business day.");
    } catch {
      toast.error("Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ai-training-page">
      {/* NAV */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/95 backdrop-blur">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="ait-brand-home">
            <ChevronLeft className="w-4 h-4 text-zinc-500" />
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black text-xl tracking-tighter">DARA</span>
            <span className="font-mono text-[10px] sm:text-[11px] text-zinc-500 ml-2 hidden sm:inline">// TRAINING DATA · FOR AI LABS</span>
          </Link>
          <a href="#ait-contact" className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-wider px-4 py-2 hover:bg-[#E59E00] transition-colors" data-testid="ait-nav-contact">
            Scope a dataset
          </a>
        </div>
      </nav>

      {/* HERO */}
      <header className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10283F]/30 via-transparent to-[#FFB000]/5 pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3 flex items-center gap-2">
            <Layers className="w-3.5 h-3.5" />
            // FRONTIER-GRADE DOMAIN DATA · BUILT INSIDE LIVE OPERATIONS
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-5xl">
            Your models aren't short on compute. <span className="text-[#FFB000]">They're short on expert data.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            DARA operates intelligence pipelines inside regulated, hard-to-reach verticals — mining exploration, wagering compliance,
            industrial operations, insurance. That position generates something no rater marketplace can:
            <span className="text-[#FFB000]"> expert-labelled decisions captured at the moment they were made, with full provenance.</span> We package it for training and evals.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#ait-contact" className="bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-[#E59E00] transition-colors flex items-center gap-2" data-testid="ait-hero-cta">
              Scope a dataset <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#ait-verticals" className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 px-5 py-3">
              See the verticals
            </a>
          </div>
        </div>
      </header>

      {/* CAPABILITIES */}
      <section className="border-b border-zinc-900">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-6 flex items-center gap-2">
            <Target className="w-3.5 h-3.5" /> // WHAT WE SUPPLY
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900" data-testid="ait-capabilities">
            {CAPABILITIES.map((c, i) => (
              <div key={i} className="bg-black p-6" data-testid={`ait-capability-${i}`}>
                <c.icon className="w-5 h-5 text-[#FFB000] mb-3" />
                <h3 className="font-heading font-bold text-base tracking-tight mb-2">{c.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VERTICALS */}
      <section className="border-b border-zinc-900" id="ait-verticals">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2 flex items-center gap-2">
            <Database className="w-3.5 h-3.5" /> // DATA MOATS · SIX VERTICALS WE ALREADY OPERATE IN
          </div>
          <p className="text-sm text-zinc-400 max-w-2xl mb-6">
            These aren't scraping targets. They're live DARA deployments — which is why the data carries decision context, expert labels, and consent.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="ait-verticals-grid">
            {VERTICALS.map((v, i) => (
              <div key={i} className="bg-black p-5 flex items-start gap-3" data-testid={`ait-vertical-${i}`}>
                <CheckCircle2 className="w-4 h-4 text-[#FFB000] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-heading font-bold text-sm">{v.name}</div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed">{v.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-zinc-900">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-6 flex items-center gap-2">
            <Gauge className="w-3.5 h-3.5" /> // ENGAGEMENT MODEL
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="ait-steps">
            {STEPS.map((s) => (
              <div key={s.n} className="border border-zinc-900 bg-zinc-950/50 p-6">
                <div className="font-mono text-3xl font-black text-[#FFB000]/30">{s.n}</div>
                <h3 className="font-heading font-bold text-lg tracking-tight mt-2 mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 border border-zinc-900 bg-zinc-950/30 p-5">
            <p className="font-mono text-[10px] text-zinc-500 leading-relaxed max-w-4xl">
              // COMMERCIALS — per-dataset licensing or standing supply agreements. Exclusivity windows available per vertical.
              All supply is bound by source-consent terms; nothing is resold outside its consent basis.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="ait-contact" className="border-b border-zinc-900">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// SCOPE A DATASET</div>
            <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight leading-tight">
              Tell us the capability gap. We'll tell you if we can close it.
            </h2>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-md">
              Data, research, and post-training teams: include target domain, task shape, and rough volume. We respond within one business day — with a straight no if our verticals don't fit.
            </p>
          </div>
          <div className="lg:col-span-7">
            {done ? (
              <div className="border border-[#FFB000]/40 bg-[#FFB000]/5 p-8 text-center" data-testid="ait-form-success">
                <CheckCircle2 className="w-10 h-10 text-[#FFB000] mx-auto mb-3" />
                <h3 className="font-heading font-bold text-xl mb-2">Received.</h3>
                <p className="text-sm text-zinc-400">We'll come back within one business day with a fit/no-fit read.</p>
              </div>
            ) : (
              <div className="border border-zinc-900 bg-zinc-950/50 p-6 space-y-3" data-testid="ait-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Jane Researcher" testId="ait-form-name" />
                  <FormField label="Work email *" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="jane@lab.ai" type="email" testId="ait-form-email" />
                  <FormField label="Lab / company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} placeholder="Frontier Lab Inc." testId="ait-form-company" />
                  <FormField label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} placeholder="Data lead · Research PM" testId="ait-form-role" />
                </div>
                <FormField
                  label="What are you trying to train or evaluate?"
                  multiline
                  value={form.message}
                  onChange={(v) => setForm({ ...form, message: v })}
                  placeholder="Domain, task shape, volume, timeline…"
                  testId="ait-form-message"
                />
                <button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="ait-form-submit"
                  className="w-full bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-[#E59E00] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  {submitting ? "Sending…" : "Send the scope"}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <footer className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
        <p className="font-mono text-[10px] text-zinc-600">// DARA OS · DATA SUPPLY DIVISION · ALL SUPPLY CONSENT-BOUND &amp; PROVENANCE-TRACKED</p>
      </footer>
    </div>
  );
}

const FormField = ({ label, value, onChange, placeholder, type = "text", multiline = false, testId }) => (
  <label className="block">
    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        data-testid={testId}
        className="w-full bg-black border border-zinc-800 focus:border-[#FFB000] outline-none px-3 py-2 text-sm text-white placeholder:text-zinc-700 transition-colors"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-black border border-zinc-800 focus:border-[#FFB000] outline-none px-3 py-2 text-sm text-white placeholder:text-zinc-700 transition-colors"
      />
    )}
  </label>
);
